import React, { useEffect, useId, useRef, useState } from 'react'
import clsx from 'clsx'
import styles from './MultiSelectDropdown.module.css'
import { useOutsideClick } from '../../../hooks/useOutsideClick'
import { useFieldId, useFieldLabelId } from '../FieldWrapper/FieldWrapper'

export interface MultiSelectOption<V extends string> {
  value: V
  label: string
}

interface Props<V extends string> {
  value: V[]
  onChange: (next: V[]) => void
  options: ReadonlyArray<MultiSelectOption<V>>
  placeholder?: string
  disabled?: boolean
  className?: string
  hasError?: boolean
}

export function MultiSelectDropdown<V extends string>({
  value,
  onChange,
  options,
  placeholder = 'Alle',
  disabled = false,
  className,
  hasError,
}: Props<V>) {
  const [open, setOpen] = useState(false)
  /** Where the arrow keys are. Not a selection — this list commits on Enter, one option at a time. */
  const [activeIndex, setActiveIndex] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  const activeOptionRef = useRef<HTMLDivElement>(null)
  const generatedId = useId()
  // Labelled by the enclosing `FieldWrapper` when there is one.
  const fieldId = useFieldId()
  // `htmlFor` cannot reach a `<div role="combobox">`; this is how the label gets attached.
  const labelId = useFieldLabelId()
  const id = fieldId ?? generatedId

  useOutsideClick(containerRef, () => setOpen(false), open)

  const openList = () => {
    if (disabled) return
    setActiveIndex((prev) => (prev >= 0 ? prev : 0))
    setOpen(true)
  }

  const toggle = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (disabled) return
    if (open) setOpen(false)
    else openList()
  }

  const commit = (v: V) => {
    if (value.includes(v)) {
      onChange(value.filter((x) => x !== v))
    } else {
      onChange([...value, v])
    }
  }

  const toggleOption = (v: V, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    commit(v)
  }

  /**
   * The keyboard contract this control had none of: it was a `role="combobox"` on a plain `<div>`
   * with no `tabIndex`, so it could not be reached by Tab at all, let alone operated.
   *
   * Enter is `preventDefault`ed for the same reason as in `Select`: forms now submit on Enter (see
   * `Modal`'s `SubmitButtonConfig.formId`), and a combobox that let it through would save the form
   * instead of ticking an option. Unlike a single select, committing here does **not** close the
   * list — picking several in a row is the point.
   *
   * Escape only stops propagating when it closed the list, so a second press still reaches the
   * dialog. `modalStack` listens on the bubble phase to make that ordering possible.
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return

    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        e.preventDefault()
        if (!open) {
          openList()
          return
        }
        const step = e.key === 'ArrowDown' ? 1 : -1
        setActiveIndex((prev) => {
          const next = prev + step
          if (next < 0) return options.length - 1
          if (next >= options.length) return 0
          return next
        })
        return
      }
      case 'Home':
      case 'End': {
        if (!open) return
        e.preventDefault()
        setActiveIndex(e.key === 'Home' ? 0 : options.length - 1)
        return
      }
      case 'Enter':
      case ' ': {
        e.preventDefault()
        if (!open) {
          openList()
          return
        }
        const option = options[activeIndex]
        if (option) commit(option.value)
        return
      }
      case 'Escape': {
        if (!open) return
        e.preventDefault()
        e.stopPropagation()
        setOpen(false)
        return
      }
      case 'Tab': {
        if (open) setOpen(false)
      }
    }
  }

  useEffect(() => {
    if (open) activeOptionRef.current?.scrollIntoView({ block: 'nearest' })
  }, [open, activeIndex])

  const displayLabel = (() => {
    if (value.length === 0) return ''
    if (value.length <= 2) {
      return value
        .map((v) => options.find((o) => o.value === v)?.label ?? v)
        .join(', ')
    }
    return `${value.length} ausgewählt`
  })()

  return (
    <div ref={containerRef} className={clsx(styles.container, className)}>
      <div
        className={clsx(
          styles.trigger,
          disabled && styles.triggerDisabled,
          hasError && styles.triggerError,
          open && styles.triggerOpen,
        )}
        onClick={toggle}
        onKeyDown={handleKeyDown}
        id={id}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={open}
        aria-disabled={disabled}
        aria-invalid={hasError || undefined}
        aria-controls={`${id}-listbox`}
        aria-labelledby={labelId}
        aria-activedescendant={
          open && activeIndex >= 0 ? `${id}-option-${activeIndex}` : undefined
        }
      >
        <span className={clsx(styles.value, !displayLabel && styles.placeholder)}>
          {displayLabel || placeholder}
        </span>
        <svg
          className={clsx(styles.chevron, open && styles.chevronOpen)}
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M5 8L10 13L15 8"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {open && !disabled && (
        <div
          id={`${id}-listbox`}
          className={styles.dropdown}
          role="listbox"
          aria-multiselectable="true"
        >
          {options.map((opt, index) => {
            const checked = value.includes(opt.value)
            const isActive = index === activeIndex
            return (
              <div
                key={opt.value}
                id={`${id}-option-${index}`}
                ref={isActive ? activeOptionRef : null}
                className={clsx(
                  styles.option,
                  checked && styles.optionChecked,
                  isActive && styles.optionActive,
                )}
                onClick={(e) => toggleOption(opt.value, e)}
                // One highlight for pointer and keyboard, so hovering does not leave a stale
                // target behind for Enter.
                onMouseEnter={() => setActiveIndex(index)}
                role="option"
                aria-selected={checked}
              >
                <span className={clsx(styles.checkbox, checked && styles.checkboxChecked)}>
                  {checked && (
                    <svg viewBox="0 0 12 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M1 5L4.5 8.5L11 1"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </span>
                {opt.label}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
